import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import { AccountsError } from './useAccounts'

/**
 * `DELETE /aggregator/items/{item_id}` — the REAL disconnect, and the only
 * one there is.
 *
 * `AccountDetail.tsx`'s button labelled "Disconnect" calls
 * `DELETE /accounts/{id}`, which the backend documents as setting
 * `is_hidden=True` because "Plaid does not support unlinking individual
 * accounts — only entire Items". That button hides; this one disconnects.
 * Renaming the other is a separate card and must land.
 *
 * The server soft-deletes the item and calls `provider.remove_item` to revoke
 * access at the bank. **It does not touch accounts** — they survive with their
 * full transaction history and simply stop updating, becoming `orphan` in
 * `connectionGroups.ts`'s own vocabulary. The confirm copy says exactly that.
 *
 * Invalidates accounts as well as items: an account's health is derived from
 * its item, so a stale accounts cache would keep rendering a connection that
 * no longer exists.
 *
 * **`isPending` is exposed for a future caller, not the current one** — NIT,
 * final review. `Connections.tsx` (the only caller today) never reads it:
 * `ConfirmDialog` already owns the in-flight state for the dialog that
 * triggers this mutation (`Dialog.tsx`'s own `busy`), so nothing here needs a
 * second flag. Left in the return type rather than dropped, matching every
 * other mutation hook in this module's shape (`useSyncItem`, `useReauthFlow`).
 */
export function useDeleteItem(): {
  remove: (itemId: string) => Promise<void>
  isPending: boolean
} {
  const client = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (itemId: string) => {
      const { error, response } = await api.DELETE('/api/v1/aggregator/items/{item_id}', {
        params: { path: { item_id: itemId } },
      })
      if (error !== undefined) throw new AccountsError(error, response.status)
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.aggregator.items() })
      void client.invalidateQueries({ queryKey: keys.accounts.all() })
    },
  })
  return { remove: (itemId) => mutation.mutateAsync(itemId), isPending: mutation.isPending }
}
